/**
 * IM-004: Resolve `inventoryIds` for Shopper Products (`getProduct` / `getProducts`) from the
 * warehouse cookie when IM-001 preference allows warehouse behaviour.
 */

import {useEffect, useMemo, useState} from 'react';

import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks';

import {usePdpWarehouseInventoryContext} from '../context/pdp-warehouse-inventory-context';
import {
  INVENTORY_CONTEXT_CHANGED_EVENT,
  resolveShopperInventoryIdsParameter,
} from '../utils/warehouse-inventory-cookies';

/**
 * Comma-separated inventory list ids for SCAPI (max 5), or `undefined` when the site should use
 * default inventory behaviour.
 *
 * @returns {string|undefined}
 */
export function useShopperInventoryIds() {
  const {isPdpScope, isActive, inventoryIds} = usePdpWarehouseInventoryContext();
  const {site} = useMultiSite();
  const serverCtx = useServerContext();
  const req = serverCtx?.req;
  const siteId = site?.id || 'kl-au';
  const preferences = site?.preferences || {};
  const warehousePrefSignature = String(preferences?.warehouseInventories ?? '');
  const cookieHeader = typeof req?.headers?.cookie === 'string' ? req.headers.cookie : '';

  const [clientTick, setClientTick] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const bump = () => setClientTick((t) => t + 1);

    window.addEventListener(INVENTORY_CONTEXT_CHANGED_EVENT, bump);

    return () => window.removeEventListener(INVENTORY_CONTEXT_CHANGED_EVENT, bump);
  }, []);

  const cookieIds = useMemo(
    () => resolveShopperInventoryIdsParameter({siteId, preferences, req}),
    [siteId, warehousePrefSignature, cookieHeader, clientTick]
  );

  // KINDW-1598: on PDP, never use checkout warehouse cookies — wait for PDP delivery resolve.
  return useMemo(() => {
    if (isPdpScope) {
      return isActive ? inventoryIds : undefined;
    }

    return cookieIds;
  }, [cookieIds, inventoryIds, isActive, isPdpScope]);
}

export default useShopperInventoryIds;
